import { api } from './api';

/**
 * Hackathon Service
 * API calls for hackathon module management
 */
const hackathonService = {
  // ============ Event Management ============

  /**
   * Create a new hackathon event
   */
  createEvent: async (eventData) => {
    const response = await api.post('/hackathon/events', eventData);
    return response.data;
  },

  /**
   * Get all hackathon events
   */
  getEvents: async (status = null) => {
    const params = status ? { status } : {};
    const response = await api.get('/hackathon/events', { params });
    return response.data;
  },

  /**
   * Get event by ID
   */
  getEvent: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}`);
    return response.data;
  },

  /**
   * Update event
   */
  updateEvent: async (eventId, updates) => {
    const response = await api.put(`/hackathon/events/${eventId}`, updates);
    return response.data;
  },

  /**
   * Update event status
   */
  updateEventStatus: async (eventId, status) => {
    const response = await api.put(`/hackathon/events/${eventId}/status?status=${status}`);
    return response.data;
  },

  /**
   * Get event statistics
   */
  getEventStats: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/stats`);
    return response.data;
  },

  // ============ KPI Management ============

  /**
   * Update KPI configuration
   */
  updateKPIs: async (eventId, kpis) => {
    const response = await api.put(`/hackathon/events/${eventId}/kpis`, kpis);
    return response.data;
  },

  /**
   * Add a new KPI
   */
  addKPI: async (eventId, kpi) => {
    const response = await api.post(`/hackathon/events/${eventId}/kpis`, kpi);
    return response.data;
  },

  // ============ Module Management ============

  /**
   * Add a module to the event
   */
  addModule: async (eventId, moduleData) => {
    const response = await api.post(`/hackathon/events/${eventId}/modules`, moduleData);
    return response.data;
  },

  /**
   * Upload document for a module
   */
  uploadModuleDocument: async (eventId, moduleId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(
      `/hackathon/events/${eventId}/modules/${moduleId}/documents`,
      formData
    );
    return response.data;
  },

  // ============ Import ============

  /**
   * Import participants from Excel
   */
  importParticipants: async (eventId, file, sendInvites = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('send_invites', sendInvites);
    const response = await api.post(`/hackathon/events/${eventId}/import`, formData);
    return response.data;
  },

  // ============ Send Invites ============

  /**
   * Send invitation emails to participants
   */
  sendInvites: async (eventId, resendAll = false) => {
    const response = await api.post(`/hackathon/events/${eventId}/send-invites`, {
      resend_all: resendAll
    });
    return response.data;
  },

  // ============ Registrations ============

  /**
   * Get all registrations for an event
   */
  getRegistrations: async (eventId, status = null, operatorRole = null) => {
    const params = {};
    if (status) params.status = status;
    if (operatorRole) params.operator_role = operatorRole;
    const response = await api.get(`/hackathon/events/${eventId}/registrations`, { params });
    return response.data;
  },

  /**
   * Get registration by ID
   */
  getRegistration: async (registrationId) => {
    const response = await api.get(`/hackathon/registrations/${registrationId}`);
    return response.data;
  },

  /**
   * Update registration
   */
  updateRegistration: async (registrationId, updates) => {
    const response = await api.put(`/hackathon/registrations/${registrationId}`, updates);
    return response.data;
  },

  /**
   * Confirm registration
   */
  confirmRegistration: async (registrationId) => {
    const response = await api.post(`/hackathon/registrations/${registrationId}/confirm`);
    return response.data;
  },

  /**
   * Decline registration
   */
  declineRegistration: async (registrationId) => {
    const response = await api.post(`/hackathon/registrations/${registrationId}/decline`);
    return response.data;
  },

  // ============ Public Registration (No Auth) ============

  /**
   * Get public registration form data
   */
  getPublicRegistration: async (registrationId) => {
    const response = await api.get(`/hackathon/public/registration/${registrationId}`);
    return response.data;
  },

  /**
   * Submit public registration form
   */
  submitPublicRegistration: async (registrationId, formData) => {
    const response = await api.put(`/hackathon/public/registration/${registrationId}`, formData);
    return response.data;
  },

  // ============ Teams ============

  /**
   * Generate teams automatically
   */
  generateTeams: async (eventId) => {
    const response = await api.post(`/hackathon/events/${eventId}/generate-teams`);
    return response.data;
  },

  /**
   * Get all teams for an event
   */
  getTeams: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/teams`);
    return response.data;
  },

  /**
   * Get team by ID
   */
  getTeam: async (teamId) => {
    const response = await api.get(`/hackathon/teams/${teamId}`);
    return response.data;
  },

  /**
   * Assign module to team
   */
  assignModuleToTeam: async (teamId, moduleId) => {
    const response = await api.post(`/hackathon/teams/${teamId}/assign-module`, { module_id: moduleId });
    return response.data;
  },

  // ============ Volunteers ============

  /**
   * Get volunteers for an event
   */
  getVolunteers: async (eventId, volunteerType = null) => {
    const params = volunteerType ? { volunteer_type: volunteerType } : {};
    const response = await api.get(`/hackathon/events/${eventId}/volunteers`, { params });
    return response.data;
  },

  /**
   * Assign role to volunteer
   */
  assignVolunteerRole: async (volunteerId, roleData) => {
    const response = await api.put(`/hackathon/volunteers/${volunteerId}/assign`, roleData);
    return response.data;
  },

  /**
   * Get volunteer roadmap
   */
  getVolunteerRoadmap: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/volunteer-roadmap`);
    return response.data;
  },

  // ============ Scoring ============

  /**
   * Submit team score (jury)
   */
  submitTeamScore: async (teamId, scoreData) => {
    const response = await api.post(`/hackathon/teams/${teamId}/score`, scoreData);
    return response.data;
  },

  /**
   * Submit individual score
   */
  submitIndividualScore: async (teamId, registrationId, kpiScores) => {
    const response = await api.post(`/hackathon/teams/${teamId}/individual-score`, {
      registration_id: registrationId,
      kpi_scores: kpiScores
    });
    return response.data;
  },

  /**
   * Get leaderboard
   */
  getLeaderboard: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/leaderboard`);
    return response.data;
  },

  /**
   * Finalize awards
   */
  finalizeAwards: async (eventId) => {
    const response = await api.post(`/hackathon/events/${eventId}/finalize-awards`);
    return response.data;
  },

  /**
   * Get awards
   */
  getAwards: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/awards`);
    return response.data;
  },

  // ============ Tracking ============

  /**
   * Sync tracking data for a team (from SQLite)
   */
  syncTeamTracking: async (teamId) => {
    const response = await api.post(`/hackathon/teams/${teamId}/sync-tracking-auto`);
    return response.data;
  },

  /**
   * Sync tracking for all teams in an event
   */
  syncAllTeamsTracking: async (eventId) => {
    const response = await api.post(`/hackathon/events/${eventId}/sync-all-tracking`);
    return response.data;
  },

  /**
   * Get team tracking dashboard
   */
  getTeamTrackingDashboard: async (teamId) => {
    const response = await api.get(`/hackathon/teams/${teamId}/tracking-dashboard`);
    return response.data;
  },

  /**
   * Get event tracking overview
   */
  getEventTrackingOverview: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/tracking-overview`);
    return response.data;
  },

  /**
   * Get auto-calculated KPIs for a team
   */
  getAutoKPIs: async (teamId) => {
    const response = await api.get(`/hackathon/teams/${teamId}/auto-kpis`);
    return response.data;
  },

  // ============ Food Preferences ============

  /**
   * Save food preferences for a registration
   */
  saveFoodPreferences: async (registrationId, preferences) => {
    const response = await api.post(`/hackathon/registrations/${registrationId}/food-preferences`, {
      preferences
    });
    return response.data;
  },

  /**
   * Get food preferences for a registration
   */
  getFoodPreferences: async (registrationId) => {
    const response = await api.get(`/hackathon/registrations/${registrationId}/food-preferences`);
    return response.data;
  },

  // ============ Volunteer Registration ============

  /**
   * Send invites to volunteers/jury
   */
  sendVolunteerInvites: async (eventId, resendAll = false) => {
    const response = await api.post(`/hackathon/events/${eventId}/send-volunteer-invites`, {
      resend_all: resendAll
    });
    return response.data;
  },

  /**
   * Get volunteer for public registration form (no auth)
   */
  getPublicVolunteer: async (volunteerId) => {
    const response = await api.get(`/hackathon/public/volunteer/${volunteerId}`);
    return response.data;
  },

  /**
   * Submit volunteer registration (no auth)
   */
  submitPublicVolunteerRegistration: async (volunteerId, formData) => {
    const response = await api.put(`/hackathon/public/volunteer/${volunteerId}`, formData);
    return response.data;
  },

  /**
   * Save food preferences for a volunteer
   */
  saveVolunteerFoodPreferences: async (volunteerId, preferences) => {
    const response = await api.post(`/hackathon/volunteers/${volunteerId}/food-preferences`, {
      preferences
    });
    return response.data;
  },

  /**
   * Get food preferences for a volunteer
   */
  getVolunteerFoodPreferences: async (volunteerId) => {
    const response = await api.get(`/hackathon/volunteers/${volunteerId}/food-preferences`);
    return response.data;
  },
  /**
   * Export food preferences to Excel for confirmed WFO registrations
   */
  exportFoodPreferences: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/export-food-preferences`, {
      responseType: 'blob'
    });

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `food_preferences_${eventId}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Export all members grouped by role (jury, volunteers, operators) to Excel
   */
  exportMembersByRole: async (eventId) => {
    const response = await api.get(`/hackathon/events/${eventId}/export-members-by-role`, {
      responseType: 'blob'
    });

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `members_by_role_${eventId}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
};

export default hackathonService;
